import { useRef, useState } from 'react'

import {
  IconArrowLeft,
  IconCamera,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react'
import { Form, Radio, message, Tooltip } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import styled from 'styled-components'

import { Button } from '@/components/shared-components/BUI/Button'
import { Text } from '@/components/shared-components/BUI/Text'
import { Uploader } from '@/components/shared-components/BUI/Uploader'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { FileUploadResult } from '@/hooks/useDocumentUpload'
import { useScreenshotCapture } from '@/hooks/useScreenshotCapture'

import { AttachmentType, Resource } from 'types/api.graphql'

interface UploaderMethods {
  addFiles: (files: File[]) => void
}

export interface FeedbackFormValues {
  message: string
  attachments: FileUploadResult[]
  wantsUpdates: boolean
  rating?: string
}

export const AttachmentWrapper = styled.div`
  position: relative;
  width: 100%;
`

// Floats inside the TextArea and triggers the file input
export const ButtonGroup = styled.div`
  position: absolute;
  right: 12px;
  bottom: 8px;
  display: flex;
  gap: 4px;
`

export const FloatingButton = styled(Button)`
  padding: 4px;
  min-width: 32px;
  height: 32px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    background: transparent;
  }

  &[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`
// Hide the default uploader UI completely
export const HiddenUploader = styled.div`
  display: none;
`

export const TextAreaStyled = styled(TextArea)`
  padding-bottom: 40px !important; // Make room for the button
  margin-top: 0.5rem;
`

export const CloseButton = styled(Button).attrs({
  variant: 'ghost',
})`
  position: absolute;
  right: 0.5rem;
  top: 1rem;
`

export const BackButton = styled(Button).attrs({
  variant: 'ghost',
})`
  position: absolute;
  left: 0.5rem;
  top: -0.5rem;

  ${({ theme: { media } }) => media.sm`
  left: 0.5rem;
  top: 1rem;
  `}
`

const FullWidthRadioGroup = styled(Radio.Group)`
  display: flex;

  & > label {
    flex-grow: 1;
    display: flex;
    justify-content: center;
  }

  & .ant-radio-button-wrapper-checked::before {
    background-color: ${({ theme }) => theme.colour.secondary._800} !important;
  }
  & .ant-radio-button-wrapper-checked {
    background-color: ${({ theme }) => theme.colour.secondary._800} !important;
    border: 1px solid ${({ theme }) => theme.colour.secondary._800} !important;
  }
`

export const FeedbackForm = ({
  projectId,
  title,
  subtitle,
  icon,
  isSubmitting,
  onBack,
  onClose,
  showRating = false,
  textPlaceHolder = 'Enter your feedback...',
}: {
  projectId?: string
  title?: string | React.ReactNode
  subtitle?: string | React.ReactNode
  icon?: React.ReactNode
  isSubmitting: boolean
  onBack?: () => void
  onClose: () => void
  showRating?: boolean
  textPlaceHolder?: string
  showLoop?: boolean
}) => {
  const { isCapturing, captureScreenshot } = useScreenshotCapture()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploaderRef = useRef<UploaderMethods>(null)

  const handleFileSelect = (files: File[]) => {
    const addFiles = uploaderRef.current?.['addFiles']
    if (addFiles) {
      addFiles(files)
    }
  }

  const isSmall = useBreakpoint('lg')

  const form = Form.useFormInstance()
  const [files, setFiles] = useState<FileUploadResult[]>([])

  const watchedTextContent = Form.useWatch('message')
  const watchedRating = Form.useWatch('rating')

  const handleScreenshotClick = async () => {
    const screenshotFile = await captureScreenshot()

    if (!screenshotFile) {
      message.error('Failed to capture screenshot')
      return
    }

    // Get reference to the Uploader's addFiles method
    // We know it exists on the ref because we set it in customUploader
    const addFiles = uploaderRef.current?.['addFiles']
    if (addFiles) {
      addFiles([screenshotFile])
    }
  }

  return (
    <Flexbox flexDirection="column" gap="0.5rem">
      {!isSmall && (
        <CloseButton
          variant="ghost"
          icon={<IconX size={16} />}
          onClick={onClose}
        />
      )}
      {onBack && (
        <BackButton icon={<IconArrowLeft size={16} />} onClick={onBack} />
      )}
      {(icon || title) && (
        <Flexbox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flexbox
            flexDirection="row"
            gap="1rem"
            justifyContent="center"
            alignItems="center"
            $fullWidth
            style={{ paddingLeft: icon ? '2rem' : '0', paddingRight: '2rem' }}
          >
            {icon}
            {title && <Text variant="subheading01">{title}</Text>}
          </Flexbox>
        </Flexbox>
      )}

      {subtitle && <Text>{subtitle}</Text>}

      {showRating && (
        <Flexbox flexDirection="column" gap="0.25rem">
          <Form.Item name="rating" noStyle>
            <FullWidthRadioGroup
              optionType="button"
              buttonStyle="outline"
              options={[
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5', value: '5' },
              ]}
            />
          </Form.Item>
          <Flexbox
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text variant="caption01" secondary="_300">
              Poor
            </Text>
            <Text variant="caption01" secondary="_300">
              Excellent
            </Text>
          </Flexbox>
        </Flexbox>
      )}

      <div style={{ position: 'relative' }}>
        <Form.Item
          name="message"
          rules={[
            {
              required: showRating ? false : true,
              message: '',
            },
          ]}
          style={{ marginBottom: '0rem' }}
        >
          <TextAreaStyled
            placeholder={textPlaceHolder}
            autoSize={{ minRows: 2 }}
          />
        </Form.Item>

        <ButtonGroup>
          <Tooltip title="Attach screenshot">
            <FloatingButton
              variant="ghost"
              icon={<IconCamera size={16} />}
              onClick={handleScreenshotClick}
              disabled={isCapturing}
              title={
                isCapturing ? 'Capturing screenshot...' : 'Take screenshot'
              }
            />
          </Tooltip>
          <Tooltip title="Attach files">
            <FloatingButton
              variant="ghost"
              icon={<IconPaperclip size={16} />}
              onClick={() => {
                fileInputRef.current?.click()
              }}
              title="Attach file"
            />
          </Tooltip>
        </ButtonGroup>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || [])
          handleFileSelect(files)
        }}
        hidden
      />

      <Form.Item name="attachments" noStyle>
        <Uploader
          projectId={projectId}
          attachmentType={AttachmentType.Media}
          attachmentResource={Resource.Attachment}
          enableMarkup
          fileList={files}
          onChange={(newFiles) => setFiles(newFiles)}
          customUploader={(_a, _b, _fileInput, addFilesMethod) => {
            uploaderRef.current = { addFiles: addFilesMethod }
            return null
          }}
        />
      </Form.Item>

      <Button
        style={{ marginTop: '0.5rem' }}
        onClick={form.submit}
        loading={isSubmitting}
        variant="primary"
        disabled={isSubmitting || (!watchedTextContent && !watchedRating)}
      >
        Send feedback
      </Button>
    </Flexbox>
  )
}
